import { useEffect, useState } from "react";

import {
  compareToNECC,
  neccRateService,
  type NECCRate,
} from "@/services/neccRateService";

interface Props {
  /** The sale's date, YYYY-MM-DD. The rate is per day, so a backdated sale
   *  must be read against the rate declared on ITS day, not today's. */
  date: string;
  /** The price being typed, as a raw string — the form's own state. */
  pricePerEgg: string;
}

/**
 * The declared NECC rate for the sale's date, beside the price being typed.
 *
 * WHY AT THE TILL. The realisation report tells a farm afterwards that a buyer
 * was 15 paise under all month. This tells them before they agree to it, which
 * is the only moment the number can still change the outcome — the same reason
 * credit control is a prompt on the sale form rather than a report.
 *
 * It NEVER blocks. A price below NECC is often a deliberate decision — a big
 * regular buyer, a bulk load, poor-quality stock — and a form that argued with
 * the farmer about their own trade would be switched off. It states the
 * comparison and gets out of the way.
 */
export default function NECCRateNotice({ date, pricePerEgg }: Props) {
  const [rate, setRate] = useState<NECCRate | null>(null);
  // Four states, not two. "Absent" and "could not be read" both arrive as a
  // null rate, and collapsing them makes the component print "No NECC rate
  // recorded for this date" — a claim about the FARM — whenever the network
  // hiccups. Same substitution this codebase has had to fix on the flock
  // chart and the Daily Report.
  const [status, setStatus] = useState<"loading" | "found" | "absent" | "failed">("loading");

  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    setStatus("loading");
    neccRateService
      .forDate(date)
      .then((r) => {
        if (cancelled) return;
        setRate(r);
        setStatus(r ? "found" : "absent");
      })
      // The benchmark is an aid: a sale must never fail to be recordable
      // because a reference price could not be fetched. So this degrades to
      // silence — but NOT to the "nothing recorded" message below.
      .catch(() => {
        if (!cancelled) setStatus("failed");
      });
    return () => {
      cancelled = true;
    };
  }, [date]);

  if (status === "loading" || status === "failed") return null;

  // No rate for the day is the NORMAL state before somebody enters it, and
  // saying so is worth a line: a farm that never sees this prompt will never
  // discover the feature exists.
  if (status === "absent" || !rate) {
    return (
      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
        No NECC rate recorded for {date}. Add it under Finance → NECC rates to
        see how this price compares.
      </p>
    );
  }

  const cmp = compareToNECC(parseFloat(pricePerEgg), rate);

  return (
    <div className="mt-2 rounded-lg bg-gray-50 px-3 py-2 text-xs dark:bg-gray-800/60">
      <span className="text-gray-600 dark:text-gray-400">
        NECC {date}
        {rate.zone ? ` · ${rate.zone}` : ""}:{" "}
        <span className="font-medium text-gray-800 dark:text-white/90">
          ₹{rate.rate_per_egg.toFixed(2)}/egg
        </span>
      </span>
      {cmp && (
        <span
          className={`ml-2 font-medium ${
            cmp.over
              ? "text-success-600 dark:text-success-400"
              : "text-warning-600 dark:text-warning-400"
          }`}
        >
          {/* Paise, not rupees. "−0.15" reads as fifteen paise to nobody; the
              farm negotiates in paise and so does this. */}
          {cmp.over ? "+" : "−"}
          {Math.abs(cmp.paise).toFixed(0)} paise ({cmp.over ? "+" : "−"}
          {Math.abs(cmp.pct).toFixed(1)}%)
        </span>
      )}
    </div>
  );
}
